import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useConcert, useUpdateConcert, useAddProgrammeItem } from '@/hooks/useConcerts'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { ProgrammeList } from '@/components/features/concerts/ProgrammeList'
import { AddProgrammeDialog } from '@/components/features/concerts/AddProgrammeDialog'
import type { AddProgrammeItemInput } from '@/services/api/concerts'
import { Button } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowLeft, Plus } from 'lucide-react'
import type { QualityScore } from '@brent/shared/types'

export function ConcertDetail() {
  const { id } = useParams()
  const concertId = Number(id)
  const navigate = useNavigate()
  const { data, isLoading, error } = useConcert(concertId)
  const { canEdit } = useAuth()
  const updateConcert = useUpdateConcert()
  const addProgrammeItem = useAddProgrammeItem()
  const { toast } = useToast()
  const [showAddProgramme, setShowAddProgramme] = useState(false)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading concert...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
        <h2 className="font-semibold text-destructive">Failed to load concert</h2>
        <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
      </div>
    )
  }

  if (!data) return null

  const { concert, programme = [], performers = [] } = data

  const handleQualityChange = async (qualityScore: QualityScore) => {
    try {
      await updateConcert.mutateAsync({
        id: concert.id,
        input: { quality_score: qualityScore },
      })
      toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update quality',
      })
    }
  }

  const handleFieldSave = async (field: string, value: string | number | null) => {
    try {
      await updateConcert.mutateAsync({
        id: concert.id,
        input: { [field]: value },
      })
      toast({ title: 'Updated', description: `${field.replace(/_/g, ' ')} updated` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : `Failed to update ${field}`,
      })
    }
  }

  const handleDateTextSave = (value: string) => handleFieldSave('date_text', value)
  const handleDateIsoSave = (value: string) => handleFieldSave('date_iso', value || null)
  const handleYearSave = (value: string) => {
    const year = parseInt(value, 10)
    if (isNaN(year) || year < 1900 || year > 2100) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Year must be between 1900 and 2100',
      })
      return
    }
    return handleFieldSave('year', year)
  }
  const handleVenueSave = (value: string) => handleFieldSave('venue_name', value)
  const handleEnsembleSave = (value: string) => handleFieldSave('ensemble_name', value)
  const handleNotesSave = (value: string) => handleFieldSave('notes', value || null)

  const handleAddProgrammeItem = async (input: AddProgrammeItemInput) => {
    try {
      await addProgrammeItem.mutateAsync({ concertId: concert.id, input })
      toast({
        title: 'Added',
        description: `Added "${input.work_title || 'item'}" to programme`,
      })
      setShowAddProgramme(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to add programme item',
      })
    }
  }

  // Compute programme match stats
  const matchedItems = programme.filter((item) => item.composer_qid && item.work_qid).length
  const matchFraction =
    programme.length > 0 ? `${matchedItems}/${programme.length} matched` : 'No items'

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/concerts">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to concerts
          </Button>
        </Link>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{concert.date_text}</h1>
            <p className="mt-1 text-muted-foreground">
              {concert.venue_name || 'Unknown venue'}
              {concert.ensemble_name && ` \u00B7 ${concert.ensemble_name}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {canEdit ? (
              <QualityDropdown
                value={concert.quality_score}
                onChange={handleQualityChange}
                disabled={updateConcert.isPending}
              />
            ) : (
              <Badge variant="secondary" className="capitalize">
                {concert.quality_score.replace('_', ' ')}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Metadata */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Details</h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Date</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={concert.date_text}
                  onSave={handleDateTextSave}
                  placeholder="Enter date..."
                  disabled={updateConcert.isPending}
                />
              ) : (
                <span className="text-sm">{concert.date_text}</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">ISO Date</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={concert.date_iso || ''}
                  onSave={handleDateIsoSave}
                  placeholder="YYYY-MM-DD"
                  disabled={updateConcert.isPending}
                />
              ) : (
                <span className="text-sm">{concert.date_iso || '-'}</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Year</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={String(concert.year)}
                  onSave={handleYearSave}
                  placeholder="Enter year..."
                  disabled={updateConcert.isPending}
                />
              ) : (
                <span className="text-sm tabular-nums">{concert.year}</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Venue</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={concert.venue_name || ''}
                  onSave={handleVenueSave}
                  placeholder="Enter venue..."
                  disabled={updateConcert.isPending}
                />
              ) : (
                <span className="text-sm">{concert.venue_name || '-'}</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Ensemble</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={concert.ensemble_name || ''}
                  onSave={handleEnsembleSave}
                  placeholder="Enter ensemble..."
                  disabled={updateConcert.isPending}
                />
              ) : (
                <span className="text-sm">{concert.ensemble_name || '-'}</span>
              )}
            </dd>
          </div>
          {concert.source_sheet && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Source Sheet</dt>
              <dd className="mt-1 text-sm">{concert.source_sheet}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Programme Match</dt>
            <dd className="mt-1 text-sm">{matchFraction}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-sm font-medium text-muted-foreground">Notes</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={concert.notes || ''}
                  onSave={handleNotesSave}
                  placeholder="Add notes..."
                  multiline
                  disabled={updateConcert.isPending}
                />
              ) : (
                <span className="text-sm">{concert.notes || '-'}</span>
              )}
            </dd>
          </div>
        </dl>
      </section>

      {/* Programme */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Programme{' '}
            <span className="text-muted-foreground font-normal">({programme.length})</span>
          </h2>
          {canEdit && (
            <Button size="sm" onClick={() => setShowAddProgramme(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Work
            </Button>
          )}
        </div>
        <ProgrammeList concertId={concert.id} programme={programme} />
      </section>

      {/* Performers */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">
          Performers{' '}
          <span className="text-muted-foreground font-normal">({performers.length})</span>
        </h2>
        {performers.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Instrument / Voice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {performers.map((perf) => (
                  <TableRow
                    key={perf.performer_id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/performers/${perf.performer_id}`)}
                  >
                    <TableCell className="font-medium">{perf.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {perf.instrument_or_voice || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No performers recorded.</p>
        )}
      </section>

      {/* Add programme item dialog */}
      <AddProgrammeDialog
        open={showAddProgramme}
        onOpenChange={setShowAddProgramme}
        onSubmit={handleAddProgrammeItem}
        isPending={addProgrammeItem.isPending}
      />
    </div>
  )
}
